import i18nFactory from '@yandex-int/i18n';
import { ApolloQueryResult } from 'apollo-client';
import { get, isEmpty, keys, startCase } from 'lodash';

import { sortRegions } from '../../service/IndoorOutdoorHelpers/IndoorOutdoorHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { PlacementsMapHelpers } from '../../service/PlacementsMap/PlacementsMap';
import { Lang } from '../../typings/common';
import {
    Dictionary,
    DisplayListItem,
    DisplayPageBlock,
    DisplayPageBlockData,
    IndoorMapCommonData,
    MapType,
    OutdoorMapCommonData,
    SelectedScreens
} from '../../typings/displayMap';
import {
    adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_pageBlocks,
    PlacementsQuery,
    PlacementsQuery_placements,
    PlacementsQuery_placements_placementBlocks_GdIndoorPlacementBlock,
    PlacementsQuery_placements_placementBlocks_GdOutdoorPlacementBlock
} from '../../typings/graphql-grid';

import * as keyset from './placementsMap.i18n';

const i18n = i18nFactory(keyset);

type OutdoorPlacementBlock = PlacementsQuery_placements_placementBlocks_GdOutdoorPlacementBlock;
type IndoorPlacementBlock = PlacementsQuery_placements_placementBlocks_GdIndoorPlacementBlock;
type PageBlock = adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_pageBlocks;

export const mapOutdoorPlacementsMapFromApiFormat = (
    placementsData: ApolloQueryResult<PlacementsQuery>
): OutdoorMapCommonData => {
    const serverPlacements = placementsData.data.placements.placements;
    const serverPlacementsBlocks =
        placementsData.data.placements.placementBlocks;

    const screenGroupsByRegion = {};
    const placements = {};
    const facilities: string[] = [];
    const operators: string[] = [];
    const dictionary = formatDictionary(placementsData.data.placements);

    serverPlacements.reduce((result, page) => {
        if (page.type === 'OUTDOOR') {
            result[page.id] = {
                id: page.id,
                operatorName: page.operatorName,
                deleted: page.isDeleted,
                caption: page.caption
            };
        }

        return result;
    }, placements);

    const goodPlacementBlocks = serverPlacementsBlocks.filter(
        (block: OutdoorPlacementBlock) => {
            return Boolean(block.geoId && block.coordinates);
        }
    );

    goodPlacementBlocks.forEach((screen: OutdoorPlacementBlock) => {
        const regionId = screen.geoId && screen.geoId.toString();
        const platformId = screen.blockId && screen.blockId.toString();
        const providerId = screen.pageId && screen.pageId.toString();

        if (!regionId) {
            return;
        }

        if (!screenGroupsByRegion[regionId]) {
            screenGroupsByRegion[regionId] = [];
        }

        const pageIsDeleted = Boolean(placements[providerId].deleted);
        let caption = i18n('caption-format', {
            format: startCase(dictionary.facility[screen.facilityType]),
            width: screen.width,
            height: screen.height
        });

        if (screen.resolution.width && screen.resolution.height) {
            caption +=
                ' ∙ ' +
                i18n('caption-resolution', {
                    width: screen.resolution.width,
                    height: screen.resolution.height
                });
        }

        if (screen.duration) {
            caption +=
                ' → ' + i18n('caption-duration', { duration: screen.duration });
        }

        const isExistFacility = facilities.some(
            item => item === dictionary.facility[screen.facilityType]
        );

        if (!isExistFacility) {
            facilities.push(dictionary.facility[screen.facilityType]);
        }

        const isExistOperator = operators.some(
            item => item === placements[providerId].caption
        );

        if (!isExistOperator) {
            operators.push(placements[providerId].caption);
        }

        screenGroupsByRegion[regionId].push({
            coordinates: PlacementsMapHelpers.formatCoordinates(
                screen.coordinates
            ),
            resolution: {
                width: screen.resolution.width || 0,
                height: screen.resolution.height || 0
            },
            id: PlacementsMapHelpers.createPageBlockId(providerId, platformId),
            address: screen.address || '',
            regionId: regionId,
            caption: caption,
            operatorName: placements[providerId].operatorName,
            transform: screen.direction,
            gid: screen.blockCaption,
            facilityType: screen.facilityType,
            duration: screen.duration,
            width: screen.width,
            height: screen.height,
            screens: [
                {
                    id: PlacementsMapHelpers.createPageBlockId(
                        providerId,
                        platformId
                    ),
                    isDeleted:
                        pageIsDeleted ||
                        Boolean(screen.isDeleted) ||
                        Boolean(screen.hidden),
                    screenWeight: 1,
                    address: screen.address || '',
                    transform: screen.direction
                }
            ]
        });
    });

    return {
        screenGroupsByRegion: screenGroupsByRegion,
        regions: sortRegions(
            Object.keys(screenGroupsByRegion).map(regionId => {
                return {
                    id: regionId,
                    text: dictionary.region[regionId].text
                };
            })
        ),
        dictionary: dictionary,
        operators,
        facilities
    };
};

export const mapIndoorPlacementsMapFromApiFormat = (
    placementsData: ApolloQueryResult<PlacementsQuery>
): IndoorMapCommonData => {
    const serverPlacements = placementsData.data.placements.placements;
    const serverPlacementsBlocks =
        placementsData.data.placements.placementBlocks;
    const screenGroupsByRegion = {};
    const placements = {};
    const byPageZone = {};
    const facilities: string[] = [];
    const zones: string[] = [];
    const dictionary = formatDictionary(placementsData.data.placements);
    const zone = get(dictionary, 'zone');

    serverPlacements.reduce((result, page) => {
        if (page.type === 'INDOOR') {
            result[page.id] = {
                id: page.id,
                name: page.operatorName,
                deleted: page.isDeleted,
                caption: page.caption
            };
        }

        return result;
    }, placements);

    const goodPlacementBlocks = serverPlacementsBlocks.filter(
        (block: OutdoorPlacementBlock) => {
            return Boolean(block.geoId && block.coordinates);
        }
    );

    goodPlacementBlocks.forEach((block: IndoorPlacementBlock) => {
        const id = PlacementsMapHelpers.createPageZoneId(
            block.pageId,
            block.zoneCategory
        );
        const geoId = block.geoId;

        const isExistFacility = facilities.some(
            item => item === dictionary.facility[block.facilityType]
        );

        if (!isExistFacility) {
            facilities.push(dictionary.facility[block.facilityType]);
        }

        const isExistZone = zones.some(
            item => zone && item === zone[block.zoneCategory]
        );

        if (!isExistZone && zone) {
            zones.push(zone[block.zoneCategory]);
        }

        if (!!geoId) {
            if (!byPageZone[geoId]) {
                byPageZone[geoId] = [];
            }

            if (!byPageZone[geoId][id]) {
                byPageZone[geoId][id] = [];
            }

            byPageZone[geoId][id].push(block);
        }
    });

    Object.keys(byPageZone).reduce((result, regionId) => {
        const groups = byPageZone[regionId];

        screenGroupsByRegion[regionId] = Object.keys(groups).map(groupId => {
            const screens = groups[groupId];
            const first = screens[0];
            const pageIsDeleted = Boolean(placements[first.pageId].deleted);

            return {
                id: groupId,
                zoneCategory: first.zoneCategory,
                facilityType: first.facilityType,
                coordinates: PlacementsMapHelpers.formatCoordinates(
                    first.coordinates
                ),
                address: first.address || '',
                gid: first.blockCaption,
                regionId: regionId,
                caption: placements[first.pageId].caption,
                screens: screens.map((screen: IndoorPlacementBlock) => {
                    return {
                        id: PlacementsMapHelpers.createPageBlockId(
                            screen.pageId,
                            screen.blockId
                        ),
                        isDeleted:
                            pageIsDeleted ||
                            Boolean(screen.isDeleted) ||
                            Boolean(screen.hidden),
                        screenWeight: 1,
                        address: first.address || '',
                        aspectRatio: screen.aspectRatio
                    };
                })
            };
        });

        return result;
    }, screenGroupsByRegion);

    return {
        screenGroupsByRegion: screenGroupsByRegion,
        regions: sortRegions(
            Object.keys(screenGroupsByRegion).map(regionId => {
                return {
                    id: regionId,
                    text: dictionary.region[regionId].text
                };
            })
        ),
        dictionary: dictionary,
        zones,
        facilities
    };
};

export const formatDictionary = (
    data: PlacementsQuery_placements
): Dictionary => {
    const regions = {};
    const facilities = {};
    const zones = {};
    let langKey = 'nameRu';

    switch (LangUtils.getCurrentLang()) {
        case Lang.en:
            langKey = 'nameEn';
            break;
        case Lang.uk:
            langKey = 'nameUa';
            break;
        case Lang.tr:
            langKey = 'nameTr';
            break;
        case Lang.ru:
        default:
            langKey = 'nameRu';
    }

    data.facilityDictionary.reduce((result, facility) => {
        result[facility.key] = facility.value;

        return result;
    }, facilities);

    data.regionDictionary.reduce((result, region) => {
        result[region.id] = {
            id: region.id,
            text: region[langKey]
        };

        return result;
    }, regions);

    if (data.zoneDictionary) {
        data.zoneDictionary.reduce((result, zone) => {
            result[zone.key] = zone.value;

            return result;
        }, zones);
    }

    return {
        region: regions,
        zone: zones,
        facility: facilities
    };
};

export const mapDisplayPageBlockFromApiFormat = (
    pageBlocks: PageBlock[]
): DisplayPageBlock[] => {
    return pageBlocks.map(
        (pageBlock: PageBlock): DisplayPageBlock => ({
            impId: pageBlock.impId.toString(),
            pageId: pageBlock.pageId.toString()
        })
    );
};

export const mapCheckedPageBlocksData = (
    mapData: OutdoorMapCommonData | IndoorMapCommonData,
    checkedByRegion: SelectedScreens,
    mapType: MapType
): DisplayPageBlockData[] => {
    const {
        screenGroupsByRegion,
        dictionary: { facility, region }
    } = mapData;

    const result: DisplayPageBlockData[] = [];
    const zone = get(mapData.dictionary, 'zone');

    keys(screenGroupsByRegion).forEach((regionId: string) => {
        if (!isEmpty(checkedByRegion[regionId])) {
            screenGroupsByRegion[regionId].forEach((item: DisplayListItem) => {
                if (checkedByRegion[regionId].includes(item.id)) {
                    const data = {
                        screensCount: item.screens.reduce(
                            (count, screen) => count + screen.screenWeight,
                            0
                        ),
                        facility: facility[item.facilityType],
                        region: region[item.regionId].text,
                        regionId: item.regionId
                    };

                    if (mapType === MapType.outdoor) {
                        result.push({
                            ...data,
                            operatorName: get(item, 'operatorName')
                        });
                    } else {
                        result.push({
                            ...data,
                            zone: zone[item.zoneCategory]
                        });
                    }
                }
            });
        }
    });

    return result;
};

export const mapDisplayPageBlockFromSelectedScreens = (
    selectedScreens: SelectedScreens
): DisplayPageBlock[] => {
    const res: DisplayPageBlock[] = [];

    keys(selectedScreens).forEach((id: string) => {
        selectedScreens[id].forEach((screen: string) => {
            res.push(PlacementsMapHelpers.parsePageBlockId(screen));
        });
    });

    return res;
};
